import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Modal,
  Typography,
  IconButton,
  Chip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { ThemePalette } from 'mui.theme';
import AiAnalyticsAPI from 'api/ai-analytics.api';
import FeedbackWidget from 'components/FeedbackWidget';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useTranslation } from 'react-i18next';
import useCustomerInfo from 'hooks/useCustomerInfo';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FuelBenchmarkEntry {
  tpg?: number;
  gph?: number;
  unit: string;
}

interface FuelUnitLabels {
  distance: string;
  fuel: string;
  production: string;
  engineHours: string;
}

interface FuelUsageBreakdownData {
  overspeed: number;
  underspeed: number;
  long_idling: number;
  pre_dumping: number;
  pre_loading: number;
  loaded_trip?: number;
  unloaded_trip?: number;
}

interface FuelByDestinationEntry {
  location_name: string;
  location_id: string | null;
  matched: boolean;
  fuel_consumed: number;
}

interface FuelByRoadEntry {
  from_location_name: string;
  to_location_name: string;
  route_id: string | null;
  road_condition: string | null;
  fully_resolved: boolean;
  fuel_consumed: number;
}

interface FuelPerTonKmVerdict {
  bumper_id: string;
  fuel_per_ton_km: number;
}

interface FuelAiAnalysisData {
  meta: {
    period: string;
    total_days: number;
    benchmark: {
      haul_truck: FuelBenchmarkEntry;
      loader: FuelBenchmarkEntry;
    };
    unit_labels?: FuelUnitLabels;
  };
  fleet_verdicts: {
    haul_trucks: {
      total_fuel_consumed: number;
      avg_tpg: number;
      fleet_avg_tpg: number;
      summary: string;
      total_potential_fuel_savings?: number;
      fuel_usage_breakdown?: FuelUsageBreakdownData;
      fuel_by_destination?: FuelByDestinationEntry[];
      fuel_by_road?: FuelByRoadEntry[];
      most_loaded_trip_fuel?: FuelPerTonKmVerdict;
      least_loaded_trip_fuel?: FuelPerTonKmVerdict;
      most_unloaded_trip_fuel?: FuelPerTonKmVerdict;
      least_unloaded_trip_fuel?: FuelPerTonKmVerdict;
      most_overspeed_fuel?: FuelPerTonKmVerdict;
      least_overspeed_fuel?: FuelPerTonKmVerdict;
      most_underspeed_fuel?: FuelPerTonKmVerdict;
      least_underspeed_fuel?: FuelPerTonKmVerdict;
      most_long_idling_fuel?: FuelPerTonKmVerdict;
      least_long_idling_fuel?: FuelPerTonKmVerdict;
      most_pre_dumping_fuel?: FuelPerTonKmVerdict;
      least_pre_dumping_fuel?: FuelPerTonKmVerdict;
      most_pre_loading_fuel?: FuelPerTonKmVerdict;
      least_pre_loading_fuel?: FuelPerTonKmVerdict;
    };
    loaders: {
      total_fuel_consumed: number;
      avg_gph: number;
      fleet_median_gph: number;
      summary: string;
      total_potential_fuel_savings?: number;
      fuel_usage_breakdown?: FuelUsageBreakdownData;
    };
  };
  assets: FuelAssetAnalysis[];
}

interface FuelAssetMetrics {
  total_fuel_consumed?: number;
  total_production_tons?: number;
  avg_fuel_efficiency_tpg?: number;
  fuel_per_ton?: number;
  fuel_per_ton_per_km?: number;
  potential_fuel_savings?: number;
  idle_fuel_pct?: number;
  active_days?: number;
  total_days?: number;
  vs_benchmark_pct?: number;
  total_engine_hours?: number;
  fuel_per_hour_gal?: number;
  loaded_trip_fuel?: number;
  unloaded_trip_fuel?: number;
  loaded_fuel_per_ton_km?: number;
  unloaded_fuel_per_ton_km?: number;
}

interface FuelAssetAnalysis {
  bumper_id: string;
  device: string;
  make: string;
  model: string;
  asset_type: 'haul_truck' | 'loader';
  rank: number;
  fuel_score: number;
  grade: string;
  score_breakdown: {
    primary_metric_value: number;
    benchmark_value: number;
    penalty_efficiency: number;
    penalty_idle_waste: number;
    penalty_consistency: number;
    data_penalty: number;
    total_penalty: number;
  };
  metrics: FuelAssetMetrics;
  scoring_reason: string;
  anomalies: Array<{
    severity: 'critical' | 'warning' | 'info';
    date?: string;
    date_range?: { from: string; to: string };
    title: string;
    detail: string;
    metric: {
      label: string;
      value: string;
      normal_range?: string;
      note?: string;
      peers_avg?: string;
      estimated_wasted_fuel?: number;
    };
  }>;
  improvement_plan: Array<{
    priority: number;
    title: string;
    body: string;
    tags: string[];
    impact_score: number;
    impact_level: 'high' | 'medium' | 'low';
    category: string;
  }>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DIAG_STEPS = [
  'Gathering fuel telemetry…',
  'Analysing haul truck tons per gallon…',
  'Calculating loader fuel burn rates…',
  'Evaluating idle fuel waste…',
  'Scoring asset fuel efficiency…',
  'Detecting fuel anomalies…',
  'Benchmarking against fleet averages…',
  'Ranking asset fuel performance…',
  'Compiling fleet fuel verdicts…',
  'Generating fuel-saving recommendations…',
];

const LOADING_INSIGHTS = [
  {
    severity: 'info' as const,
    stat: '4.3 t/gal',
    headline: 'Fleet Avg Fuel Efficiency',
    detail:
      'Haul truck fuel efficiency is being compared against the trailing 30-day fleet benchmark.',
  },
  {
    severity: 'warning' as const,
    stat: '–14%',
    headline: 'Idle Fuel Waste',
    detail:
      'Several assets are burning a disproportionate share of fuel while idling, reducing overall efficiency.',
  },
  {
    severity: 'info' as const,
    stat: '6.8 gal/hr',
    headline: 'Loader Fuel Benchmark',
    detail:
      'Loader fuel consumption per engine hour is being scored against the fleet median.',
  },
  {
    severity: 'warning' as const,
    stat: '27%',
    headline: 'Fuel Efficiency Spread',
    detail:
      'A wide spread between the most and least fuel-efficient assets often points to a maintenance or dispatch issue.',
  },
  {
    severity: 'info' as const,
    stat: 'Top 1',
    headline: 'Most Fuel-Efficient Asset',
    detail:
      'Identifying the top fuel performer helps set a realistic efficiency template for the rest of the fleet.',
  },
];

const getGradeColors = (score: number, dark: boolean) => {
  if (score >= 85)
    return {
      text: '#27500A',
      bg: dark ? 'rgba(99,153,34,0.18)' : '#EAF3DE',
      bar: '#639922',
    };
  if (score >= 75)
    return {
      text: '#0F6E56',
      bg: dark ? 'rgba(29,158,117,0.18)' : '#E1F5EE',
      bar: '#1D9E75',
    };
  if (score >= 65)
    return {
      text: '#0C447C',
      bg: dark ? 'rgba(55,138,221,0.18)' : '#E6F1FB',
      bar: '#378ADD',
    };
  if (score >= 50)
    return {
      text: '#854F0B',
      bg: dark ? 'rgba(239,159,39,0.18)' : '#FAEEDA',
      bar: '#EF9F27',
    };
  if (score >= 35)
    return {
      text: '#A32D2D',
      bg: dark ? 'rgba(226,75,74,0.18)' : '#FCEBEB',
      bar: '#E24B4A',
    };
  return {
    text: '#888780',
    bg: dark ? 'rgba(136,135,128,0.18)' : '#F1EFE8',
    bar: '#888780',
  };
};

const SEVERITY_COLORS = {
  critical: '#E24B4A',
  warning: '#EF9F27',
  info: '#378ADD',
};

const IMPACT_COLORS = {
  high: '#E24B4A',
  medium: '#EF9F27',
  low: '#378ADD',
};

const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  operations: { bg: '#E6F1FB', text: '#0C447C' },
  data: { bg: '#FAEEDA', text: '#854F0B' },
  fuel: { bg: '#E1F5EE', text: '#0F6E56' },
  maintenance: { bg: '#FCEBEB', text: '#A32D2D' },
  scheduling: { bg: '#EAF3DE', text: '#27500A' },
  capex: { bg: '#F1EFE8', text: '#5F5E5A' },
  'best-practice': { bg: '#EAF3DE', text: '#27500A' },
};

const IMPERIAL_UNIT_LABELS: FuelUnitLabels = {
  distance: 'mi',
  fuel: 'gal',
  production: 'ton',
  engineHours: 'hr',
};
const METRIC_UNIT_LABELS: FuelUnitLabels = {
  distance: 'km',
  fuel: 'liters',
  production: 'ton',
  engineHours: 'hr',
};

const BREAKDOWN_COLORS: Record<
  keyof FuelUsageBreakdownData,
  { light: string; dark: string }
> = {
  overspeed: { light: '#2a78d6', dark: '#3987e5' },
  underspeed: { light: '#eb6834', dark: '#d95926' },
  long_idling: { light: '#1baf7a', dark: '#199e70' },
  pre_dumping: { light: '#eda100', dark: '#c98500' },
  pre_loading: { light: '#e87ba4', dark: '#d55181' },
  loaded_trip: { light: '#008300', dark: '#008300' },
  unloaded_trip: { light: '#4a3aa7', dark: '#9085e9' },
};

const BREAKDOWN_LABEL_KEYS: Record<keyof FuelUsageBreakdownData, string> = {
  overspeed: 'fuel_usage_event_overspeeding',
  underspeed: 'fuel_usage_event_underspeeding',
  long_idling: 'fuel_usage_event_long_idling',
  pre_dumping: 'fuel_usage_event_queuing_pre_dumping',
  pre_loading: 'fuel_usage_event_queuing_pre_loading',
  loaded_trip: 'fuel_usage_dropdown_loaded_fuel_consumption',
  unloaded_trip: 'fuel_usage_dropdown_unloaded_fuel_consumption',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SeverityIcon({
  severity,
}: {
  severity: 'critical' | 'warning' | 'info';
}) {
  const color = SEVERITY_COLORS[severity];
  if (severity === 'critical')
    return <ErrorOutlineIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
  if (severity === 'warning')
    return <WarningAmberIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
  return <InfoOutlinedIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
}

function MetricGrid({
  metrics,
  assetType,
  enableDarkTheme,
  unitLabels,
  benchmarkUnit,
}: {
  metrics: FuelAssetMetrics;
  assetType: 'haul_truck' | 'loader';
  enableDarkTheme: boolean;
  unitLabels: FuelUnitLabels;
  benchmarkUnit: string;
}) {
  const { t } = useTranslation();
  const cellBg = enableDarkTheme ? 'rgba(255,255,255,0.04)' : '#F5F7FA';
  const labelColor = enableDarkTheme ? '#8A92A0' : '#616161';
  const valueColor = enableDarkTheme ? '#fff' : '#212121';
  const fuelUnit = unitLabels.fuel;
  const distanceUnit = unitLabels.distance;
  const productionUnit = unitLabels.production;
  const engineHoursUnit = unitLabels.engineHours;

  const rows =
    assetType === 'haul_truck'
      ? [
          {
            label: t('total_fuel_consumed'),
            value: metrics.total_fuel_consumed
              ? `${metrics.total_fuel_consumed.toLocaleString()} ${fuelUnit}`
              : '—',
          },
          {
            label: t('fuel_usage_dropdown_loaded_fuel_consumption'),
            value:
              metrics.loaded_trip_fuel != null
                ? `${metrics.loaded_trip_fuel.toLocaleString()} ${fuelUnit}`
                : '—',
          },
          {
            label: t('fuel_usage_dropdown_unloaded_fuel_consumption'),
            value:
              metrics.unloaded_trip_fuel != null
                ? `${metrics.unloaded_trip_fuel.toLocaleString()} ${fuelUnit}`
                : '—',
          },
          {
            label: t('total_production'),
            value: metrics.total_production_tons
              ? `${metrics.total_production_tons.toLocaleString()} ${productionUnit}`
              : '—',
          },
          {
            label: t('fuel_efficiency'),
            value: metrics.avg_fuel_efficiency_tpg
              ? `${metrics.avg_fuel_efficiency_tpg} ${benchmarkUnit}`
              : '—',
          },
          {
            label: t('fuel_per_ton'),
            value:
              metrics.fuel_per_ton != null
                ? `${metrics.fuel_per_ton} ${fuelUnit}/${productionUnit}`
                : '—',
          },
          {
            label: t('fuel_per_ton_per_distance', { unit: distanceUnit }),
            value:
              metrics.fuel_per_ton_per_km != null
                ? `${metrics.fuel_per_ton_per_km} ${fuelUnit}/${productionUnit}/${distanceUnit}`
                : '—',
          },
          {
            label: t('idle_fuel_pct'),
            value:
              metrics.idle_fuel_pct != null ? `${metrics.idle_fuel_pct}%` : '—',
          },
          {
            label: t('active_days'),
            value:
              metrics.active_days != null
                ? `${metrics.active_days}/${metrics.total_days}`
                : '—',
          },
          {
            label: t('vs_benchmark'),
            value:
              metrics.vs_benchmark_pct != null
                ? `${metrics.vs_benchmark_pct > 0 ? '+' : ''}${
                    metrics.vs_benchmark_pct
                  }%`
                : '—',
          },
          {
            label: t('fuel_usage_potential_fuel_savings'),
            value:
              metrics.potential_fuel_savings != null
                ? `${metrics.potential_fuel_savings.toLocaleString()} ${fuelUnit}`
                : '—',
          },
        ]
      : [
          {
            label: t('total_fuel_consumed'),
            value: metrics.total_fuel_consumed
              ? `${metrics.total_fuel_consumed.toLocaleString()} ${fuelUnit}`
              : '—',
          },
          {
            label: t('engine_hours_2'),
            value: metrics.total_engine_hours
              ? `${metrics.total_engine_hours} ${engineHoursUnit}`
              : '—',
          },
          {
            label: t('fuel_hr'),
            value: metrics.fuel_per_hour_gal
              ? `${metrics.fuel_per_hour_gal} ${benchmarkUnit}`
              : '—',
          },
          {
            label: t('idle_fuel_pct'),
            value:
              metrics.idle_fuel_pct != null ? `${metrics.idle_fuel_pct}%` : '—',
          },
          {
            label: t('active_days'),
            value:
              metrics.active_days != null
                ? `${metrics.active_days}/${metrics.total_days}`
                : '—',
          },
          {
            label: t('vs_benchmark'),
            value:
              metrics.vs_benchmark_pct != null
                ? `${metrics.vs_benchmark_pct > 0 ? '+' : ''}${
                    metrics.vs_benchmark_pct
                  }%`
                : '—',
          },
          {
            label: t('fuel_usage_potential_fuel_savings'),
            value:
              metrics.potential_fuel_savings != null
                ? `${metrics.potential_fuel_savings.toLocaleString()} ${fuelUnit}`
                : '—',
          },
        ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '6px',
      }}
    >
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{ background: cellBg, borderRadius: '6px', p: '8px 10px' }}
        >
          <Typography sx={{ fontSize: '10px', color: labelColor, mb: '2px' }}>
            {row.label}
          </Typography>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: valueColor }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function RoadFuelUsageSection({
  roads,
  fuelUnit,
  enableDarkTheme,
}: {
  roads: FuelByRoadEntry[];
  fuelUnit: string;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';

  const resolved = roads.filter((r) => r.fully_resolved);
  const totalFuel = roads.reduce((sum, r) => sum + r.fuel_consumed, 0);
  const unresolvedFuel = roads
    .filter((r) => !r.fully_resolved)
    .reduce((sum, r) => sum + r.fuel_consumed, 0);
  const unresolvedPct = totalFuel > 0 ? (unresolvedFuel / totalFuel) * 100 : 0;

  const mostRoad = resolved[0];
  const leastRoad = resolved[resolved.length - 1];

  const roadLabel = (road: FuelByRoadEntry) =>
    `${road.from_location_name} → ${road.to_location_name}`;

  return (
    <Box sx={{ mt: '12px' }}>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: textPrimary,
          mb: '10px',
        }}
      >
        {t('fuel_by_road_title')}
      </Typography>
      {resolved.length === 0 ? (
        <Typography sx={{ fontSize: '12px', color: textSecondary }}>
          {t('no_resolved_road_data')}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            mb: '8px',
          }}
        >
          <Box
            sx={{ background: '#FCEBEB', borderRadius: '7px', p: '8px 10px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#A32D2D', mb: '2px' }}>
              {t('most_fuel_using_road')}
            </Typography>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: '#A32D2D' }}
            >
              {roadLabel(mostRoad)}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#A32D2D', mt: '1px' }}>
              {mostRoad.fuel_consumed.toLocaleString()} {fuelUnit}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#EAF3DE', borderRadius: '7px', p: '8px 10px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#3B6D11', mb: '2px' }}>
              {t('least_fuel_using_road')}
            </Typography>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: '#27500A' }}
            >
              {roadLabel(leastRoad)}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#27500A', mt: '1px' }}>
              {leastRoad.fuel_consumed.toLocaleString()} {fuelUnit}
            </Typography>
          </Box>
        </Box>
      )}
      {unresolvedFuel > 0 && (
        <Typography
          sx={{
            fontSize: '10px',
            color: textSecondary,
            fontStyle: 'italic',
          }}
        >
          {t('fuel_usage_unresolved_road_note', {
            pct: unresolvedPct.toFixed(1),
          })}
        </Typography>
      )}
    </Box>
  );
}

interface TripFuelPerTonKmExtreme {
  bumper_id: string;
  value: number;
}

function findTripFuelPerTonKmExtremes(
  trucks: FuelAssetAnalysis[],
  key: 'loaded_fuel_per_ton_km' | 'unloaded_fuel_per_ton_km'
): {
  most: TripFuelPerTonKmExtreme | null;
  least: TripFuelPerTonKmExtreme | null;
} {
  const entries: TripFuelPerTonKmExtreme[] = [];
  trucks.forEach((truck) => {
    const value = truck.metrics[key];
    if (value != null && value > 0) {
      entries.push({ bumper_id: truck.bumper_id, value });
    }
  });

  if (entries.length === 0) return { most: null, least: null };

  return {
    most: entries.reduce((max, e) => (e.value > max.value ? e : max)),
    least: entries.reduce((min, e) => (e.value < min.value ? e : min)),
  };
}

function TripFuelExtremeBox({
  label,
  extreme,
  fuelPerTonKmUnit,
  color,
  enableDarkTheme,
}: {
  label: string;
  extreme: TripFuelPerTonKmExtreme | null;
  fuelPerTonKmUnit: string;
  color: 'red' | 'green';
  enableDarkTheme: boolean;
}) {
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const bg = color === 'red' ? '#FCEBEB' : '#EAF3DE';
  const labelColor = color === 'red' ? '#A32D2D' : '#3B6D11';
  const valueColor = color === 'red' ? '#A32D2D' : '#27500A';

  return (
    <Box sx={{ background: bg, borderRadius: '7px', p: '8px 10px' }}>
      <Typography sx={{ fontSize: '10px', color: labelColor, mb: '2px' }}>
        {label}
      </Typography>
      {extreme ? (
        <>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 700, color: valueColor }}
          >
            {extreme.bumper_id}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: valueColor, mt: '1px' }}>
            {extreme.value.toFixed(6)} {fuelPerTonKmUnit}
          </Typography>
        </>
      ) : (
        <Typography
          sx={{ fontSize: '13px', fontWeight: 700, color: textSecondary }}
        >
          --
        </Typography>
      )}
    </Box>
  );
}

function toTripFuelExtreme(
  verdict?: FuelPerTonKmVerdict
): TripFuelPerTonKmExtreme | null {
  if (!verdict) return null;
  return { bumper_id: verdict.bumper_id, value: verdict.fuel_per_ton_km };
}

function EventFuelExtremesSection({
  haulTrucks,
  trucks,
  unitLabels,
  enableDarkTheme,
}: {
  haulTrucks: FuelAiAnalysisData['fleet_verdicts']['haul_trucks'];
  trucks: FuelAssetAnalysis[];
  unitLabels: FuelUnitLabels;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const sectionBg = enableDarkTheme ? 'rgba(94,133,240,0.06)' : '#F4F7FE';
  const sectionBorder = enableDarkTheme ? '#2F445E' : '#DDE6FB';
  const iconBg = enableDarkTheme ? 'rgba(94,133,240,0.18)' : '#E6ECFC';
  const fuelPerTonKmUnit = `${unitLabels.fuel}/${unitLabels.production}/${unitLabels.distance}`;
  const loadedTrip = findTripFuelPerTonKmExtremes(
    trucks,
    'loaded_fuel_per_ton_km'
  );
  const unloadedTrip = findTripFuelPerTonKmExtremes(
    trucks,
    'unloaded_fuel_per_ton_km'
  );

  const rows: Array<{
    labelKey: string;
    color: { light: string; dark: string };
    most: TripFuelPerTonKmExtreme | null;
    least: TripFuelPerTonKmExtreme | null;
  }> = [
    {
      labelKey: BREAKDOWN_LABEL_KEYS.loaded_trip,
      color: BREAKDOWN_COLORS.loaded_trip,
      most: loadedTrip.most,
      least: loadedTrip.least,
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.unloaded_trip,
      color: BREAKDOWN_COLORS.unloaded_trip,
      most: unloadedTrip.most,
      least: unloadedTrip.least,
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.overspeed,
      color: BREAKDOWN_COLORS.overspeed,
      most: toTripFuelExtreme(haulTrucks.most_overspeed_fuel),
      least: toTripFuelExtreme(haulTrucks.least_overspeed_fuel),
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.underspeed,
      color: BREAKDOWN_COLORS.underspeed,
      most: toTripFuelExtreme(haulTrucks.most_underspeed_fuel),
      least: toTripFuelExtreme(haulTrucks.least_underspeed_fuel),
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.long_idling,
      color: BREAKDOWN_COLORS.long_idling,
      most: toTripFuelExtreme(haulTrucks.most_long_idling_fuel),
      least: toTripFuelExtreme(haulTrucks.least_long_idling_fuel),
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.pre_dumping,
      color: BREAKDOWN_COLORS.pre_dumping,
      most: toTripFuelExtreme(haulTrucks.most_pre_dumping_fuel),
      least: toTripFuelExtreme(haulTrucks.least_pre_dumping_fuel),
    },
    {
      labelKey: BREAKDOWN_LABEL_KEYS.pre_loading,
      color: BREAKDOWN_COLORS.pre_loading,
      most: toTripFuelExtreme(haulTrucks.most_pre_loading_fuel),
      least: toTripFuelExtreme(haulTrucks.least_pre_loading_fuel),
    },
  ];

  const hasAnyData = rows.some((row) => row.most ?? row.least);
  if (!hasAnyData) return null;

  return (
    <Box
      sx={{
        mb: '8px',
        border: `1px solid ${sectionBorder}`,
        borderRadius: '10px',
        background: sectionBg,
        p: '14px 16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mb: '12px',
        }}
      >
        <Box
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            background: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SpeedIcon sx={{ fontSize: 14, color: '#5E85F0' }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '12.5px',
              fontWeight: 700,
              color: textPrimary,
              lineHeight: 1.3,
            }}
          >
            {t('event_fuel_usage_title')}
          </Typography>
          <Typography
            sx={{ fontSize: '10px', color: textSecondary, lineHeight: 1.3 }}
          >
            {t('event_fuel_usage_subtitle', { unit: fuelPerTonKmUnit })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '14px',
        }}
      >
        {rows.map((row) => (
          <Box key={row.labelKey} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                mb: '6px',
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '2px',
                  background: enableDarkTheme
                    ? row.color.dark
                    : row.color.light,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: '11px', fontWeight: 600, color: textPrimary }}
              >
                {t(row.labelKey)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: '8px',
              }}
            >
              <TripFuelExtremeBox
                label={t('best_asset_label')}
                extreme={row.most}
                fuelPerTonKmUnit={fuelPerTonKmUnit}
                color="green"
                enableDarkTheme={enableDarkTheme}
              />
              <TripFuelExtremeBox
                label={t('worst_asset_label')}
                extreme={row.least}
                fuelPerTonKmUnit={fuelPerTonKmUnit}
                color="red"
                enableDarkTheme={enableDarkTheme}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function AssetCard({
  asset,
  enableDarkTheme,
  period,
  unitLabels,
  benchmark,
}: {
  asset: FuelAssetAnalysis;
  enableDarkTheme: boolean;
  period: string;
  unitLabels: FuelUnitLabels;
  benchmark: { haul_truck: FuelBenchmarkEntry; loader: FuelBenchmarkEntry };
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(0);
  const grade = getGradeColors(asset.fuel_score, enableDarkTheme);
  const benchmarkUnit =
    asset.asset_type === 'haul_truck'
      ? benchmark.haul_truck.unit
      : benchmark.loader.unit;

  const borderColor = enableDarkTheme ? '#2F445E' : '#E0E4EC';
  const cardBg = enableDarkTheme ? 'rgba(255,255,255,0.02)' : '#FFFFFF';
  const expandBg = enableDarkTheme ? 'rgba(255,255,255,0.03)' : '#F8F9FB';
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const tabIndicator = '#5E85F0';

  const TAB_NAMES = [
    'Metrics',
    'Scoring Reason',
    'Anomalies',
    'Action Plan',
  ] as const;

  const buildAssetMetadata = (): Record<string, string> => {
    const base: Record<string, string> = {
      context: 'assetVerdict',
      bumper_id: asset.bumper_id,
      device: asset.device,
      make: asset.make,
      model: asset.model,
      asset_type: asset.asset_type,
      grade: asset.grade,
      fuel_score: String(asset.fuel_score),
      rank: String(asset.rank),
      period,
      active_tab: TAB_NAMES[tab],
    };

    if (tab === 0) {
      return { ...base, metrics: JSON.stringify(asset.metrics) };
    }
    if (tab === 1) {
      return { ...base, scoring_reason: asset.scoring_reason };
    }
    if (tab === 2) {
      return {
        ...base,
        anomalies: JSON.stringify(
          asset.anomalies.map((a) => ({
            title: a.title,
            detail: a.detail,
            severity: a.severity,
          }))
        ),
      };
    }
    return {
      ...base,
      improvement_plan: JSON.stringify(
        asset.improvement_plan.map((p) => ({
          title: p.title,
          body: p.body,
          priority: String(p.priority),
          impact_level: p.impact_level,
        }))
      ),
    };
  };

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        mb: '8px',
        overflow: 'hidden',
        background: cardBg,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: enableDarkTheme
            ? '0 2px 12px rgba(0,0,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Card top row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          px: '15px',
          py: '12px',
          cursor: 'pointer',
          '&:hover': {
            background: enableDarkTheme
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.02)',
          },
        }}
        onClick={() => {
          setExpanded((p) => !p);
        }}
      >
        {/* Rank */}
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 500,
            color: grade.bar,
            minWidth: '24px',
          }}
        >
          {asset.rank}
        </Typography>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600, color: textPrimary }}
            >
              {asset.bumper_id}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: textSecondary }}>
              {asset.make} {asset.model}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                px: '8px',
                py: '2px',
                borderRadius: '20px',
                background: grade.bg,
                color: grade.text,
                border: `1px solid ${grade.bar}55`,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: grade.bar,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 800,
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                {asset.grade}
              </Box>
            </Box>
            {asset.anomalies.some((a) => a.severity === 'critical') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <ErrorOutlineIcon
                  sx={{ fontSize: 13, color: SEVERITY_COLORS.critical }}
                />
              </Box>
            )}
          </Box>

          {/* Score bar */}
          <Box
            sx={{
              height: '4px',
              background: enableDarkTheme
                ? 'rgba(255,255,255,0.08)'
                : '#E8EBF0',
              borderRadius: '2px',
              mt: '7px',
              overflow: 'hidden',
              maxWidth: '200px',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${asset.fuel_score}%`,
                borderRadius: '2px',
                background: grade.bar,
                transition: 'width 0.6s ease',
              }}
            />
          </Box>
        </Box>

        {/* Score block */}
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: '26px',
              fontWeight: 500,
              color: grade.bar,
              lineHeight: 1,
            }}
          >
            {asset.fuel_score}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: textSecondary }}>
            {t('100')}
          </Typography>
        </Box>

        {/* Chevron */}
        <Box sx={{ color: textSecondary, flexShrink: 0 }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      </Box>

      {/* Expanded content */}
      {expanded && (
        <Box
          sx={{ borderTop: `1px solid ${borderColor}`, background: expandBg }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
            }}
            variant="fullWidth"
            sx={{
              borderBottom: `1px solid ${borderColor}`,
              minHeight: '36px',
              '& .MuiTab-root': {
                fontSize: '12px',
                minHeight: '36px',
                py: 0,
                color: textSecondary,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: `${textPrimary} !important`,
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': { backgroundColor: tabIndicator },
            }}
          >
            <Tab label={t('metrics')} />
            <Tab label={t('scoring_reason')} />
            <Tab label={`${t('anomalies')} (${asset.anomalies.length})`} />
            <Tab
              label={`${t('action_plan')} (${asset.improvement_plan.length})`}
            />
          </Tabs>

          <Box sx={{ p: '13px 15px' }}>
            {tab === 0 && (
              <MetricGrid
                metrics={asset.metrics}
                assetType={asset.asset_type}
                enableDarkTheme={enableDarkTheme}
                unitLabels={unitLabels}
                benchmarkUnit={benchmarkUnit}
              />
            )}

            {tab === 1 && (
              <Box
                sx={{
                  background: enableDarkTheme
                    ? 'rgba(255,255,255,0.04)'
                    : '#F5F7FA',
                  borderRadius: '7px',
                  p: '10px 12px',
                  fontSize: '12px',
                  color: textSecondary,
                  lineHeight: 1.7,
                }}
              >
                {asset.scoring_reason}
              </Box>
            )}

            {tab === 2 && (
              <Box>
                {asset.anomalies.length === 0 ? (
                  <Typography sx={{ fontSize: '12px', color: textSecondary }}>
                    {t('no_anomalies_detected')}
                  </Typography>
                ) : (
                  asset.anomalies.map((a, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        gap: '8px',
                        mb: '8px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <SeverityIcon severity={a.severity} />
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: textPrimary,
                            }}
                          >
                            {a.title}
                          </Typography>
                          {(a.date ?? a.date_range) && (
                            <Typography
                              sx={{ fontSize: '10px', color: textSecondary }}
                            >
                              {a.date ??
                                `${a.date_range?.from} – ${a.date_range?.to}`}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: textSecondary,
                            lineHeight: 1.6,
                            mt: '1px',
                          }}
                        >
                          {a.detail}
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            mt: '3px',
                            fontSize: '11px',
                            background: enableDarkTheme
                              ? 'rgba(255,255,255,0.06)'
                              : '#EBEBEB',
                            px: '7px',
                            py: '1px',
                            borderRadius: '4px',
                            color: textSecondary,
                          }}
                        >
                          {a.metric.label}: {a.metric.value}
                          {a.metric.normal_range != null &&
                            ` · ${t('normal_label')}: ${a.metric.normal_range}`}
                          {a.metric.peers_avg != null &&
                            ` · ${t('peers_avg_label')}: ${a.metric.peers_avg}`}
                          {a.metric.estimated_wasted_fuel != null &&
                            ` · ${t(
                              'est_wasted_fuel'
                            )}: ${a.metric.estimated_wasted_fuel.toLocaleString()} ${
                              unitLabels.fuel
                            }`}
                        </Box>
                        {a.metric.note != null && (
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: textSecondary,
                              mt: '3px',
                              fontStyle: 'italic',
                            }}
                          >
                            {a.metric.note}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 3 && (
              <Box>
                {asset.improvement_plan.map((plan, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      mb: '14px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: '1px',
                        background: IMPACT_COLORS[plan.impact_level] + '22',
                        color: IMPACT_COLORS[plan.impact_level],
                      }}
                    >
                      {plan.priority}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: textPrimary,
                          mb: '2px',
                        }}
                      >
                        {plan.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: textSecondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {plan.body}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '5px',
                          flexWrap: 'wrap',
                          mt: '5px',
                        }}
                      >
                        {plan.tags.map((tag) => {
                          const tc = TAG_COLORS[tag] ?? {
                            bg: '#E8EBF0',
                            text: '#616161',
                          };
                          return (
                            <Box
                              key={tag}
                              sx={{
                                fontSize: '10px',
                                fontWeight: 500,
                                px: '7px',
                                py: '1px',
                                borderRadius: '20px',
                                background: tc.bg,
                                color: tc.text,
                              }}
                            >
                              {tag}
                            </Box>
                          );
                        })}
                      </Box>
                      {/* Impact bar */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '10px',
                          color: textSecondary,
                          mt: '6px',
                          mb: '2px',
                        }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>
                          {plan.impact_level} {t('impact_word')}
                        </span>
                        <span>{plan.impact_score}/100</span>
                      </Box>
                      <Box
                        sx={{
                          height: '3px',
                          background: enableDarkTheme
                            ? 'rgba(255,255,255,0.08)'
                            : '#E8EBF0',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${plan.impact_score}%`,
                            background: IMPACT_COLORS[plan.impact_level],
                            borderRadius: '2px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Feedback */}
          <Box
            sx={{
              px: '15px',
              pb: '12px',
              pt: '4px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <FeedbackWidget
              featureName="fuelAIAnalysis"
              metadata={buildAssetMetadata()}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Fleet Action Plans Dialog ────────────────────────────────────────────────

function FleetActionPlansDialog({
  open,
  onClose,
  title,
  assets,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  assets: FuelAssetAnalysis[];
}) {
  const { t } = useTranslation();
  const assetsWithPlans = assets
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .filter((a) => a.improvement_plan.length > 0);

  return (
    <CustomMuiThemeProvider>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '20px',
            py: '14px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: '20px',
            py: '16px',
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '3px',
              background: 'divider',
            },
          }}
        >
          {assetsWithPlans.length === 0 ? (
            <Typography
              sx={{ fontSize: '13px', color: 'text.secondary', py: '8px' }}
            >
              {t('no_action_plan_items_across_this_fleet')}
            </Typography>
          ) : (
            assetsWithPlans.map((asset) => (
              <Box key={asset.bumper_id} sx={{ mb: '24px' }}>
                {/* Asset header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    mb: '10px',
                    pb: '8px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {asset.bumper_id}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '11px', color: 'text.secondary' }}
                  >
                    {asset.make} {asset.model} {t('rank')}
                    {asset.rank} {t('score')} {asset.fuel_score}/100
                  </Typography>
                </Box>

                {/* Plans for this asset */}
                {asset.improvement_plan.map((plan, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: '12px',
                      mb: '14px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: '1px',
                        background: IMPACT_COLORS[plan.impact_level] + '22',
                        color: IMPACT_COLORS[plan.impact_level],
                      }}
                    >
                      {plan.priority}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: '2px',
                        }}
                      >
                        {plan.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: 'text.secondary',
                          lineHeight: 1.65,
                        }}
                      >
                        {plan.body}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '5px',
                          flexWrap: 'wrap',
                          mt: '5px',
                        }}
                      >
                        {plan.tags.map((tag) => {
                          const tc = TAG_COLORS[tag] ?? {
                            bg: '#E8EBF0',
                            text: '#616161',
                          };
                          return (
                            <Box
                              key={tag}
                              sx={{
                                fontSize: '10px',
                                fontWeight: 500,
                                px: '7px',
                                py: '1px',
                                borderRadius: '20px',
                                background: tc.bg,
                                color: tc.text,
                              }}
                            >
                              {tag}
                            </Box>
                          );
                        })}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '10px',
                          color: 'text.secondary',
                          mt: '6px',
                          mb: '3px',
                        }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>
                          {plan.impact_level} {t('impact_word')}
                        </span>
                        <span>{plan.impact_score}/100</span>
                      </Box>
                      <Box
                        sx={{
                          height: '3px',
                          bgcolor: 'action.hover',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${plan.impact_score}%`,
                            background: IMPACT_COLORS[plan.impact_level],
                            borderRadius: '2px',
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          mt: '8px',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <FeedbackWidget
                          featureName="fuelAIAnalysis"
                          label={t('is_this_action_plan_helpful')}
                          metadata={{
                            context: 'actionPlan',
                            bumper_id: asset.bumper_id,
                            asset_type: asset.asset_type,
                            rank: String(asset.rank),
                            fuel_score: String(asset.fuel_score),
                            plan_title: plan.title,
                            plan_priority: String(plan.priority),
                            impact_level: plan.impact_level,
                            impact_score: String(plan.impact_score),
                            tags: plan.tags.join(', '),
                            category: plan.category,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </CustomMuiThemeProvider>
  );
}

// ─── Loading Screen ───────────────────────────────────────────────────────────

function LoadingScreen({ enableDarkTheme }: { enableDarkTheme: boolean }) {
  const { t } = useTranslation();
  const [diagStep, setDiagStep] = useState(0);
  const [diagVisible, setDiagVisible] = useState(true);
  const [insightIndex, setInsightIndex] = useState(0);
  const [insightVisible, setInsightVisible] = useState(true);

  useEffect(() => {
    const iv = setInterval(() => {
      setDiagVisible(false);
      setTimeout(() => {
        setDiagStep((s) => (s + 1) % DIAG_STEPS.length);
        setDiagVisible(true);
      }, 300);
    }, 1800);
    return () => {
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setInsightVisible(false);
      setTimeout(() => {
        setInsightIndex((i) => (i + 1) % LOADING_INSIGHTS.length);
        setInsightVisible(true);
      }, 400);
    }, 3000);
    return () => {
      clearInterval(iv);
    };
  }, []);

  const insight = LOADING_INSIGHTS[insightIndex];
  const severityColor = SEVERITY_COLORS[insight.severity];

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const surface = enableDarkTheme ? '#111c2d' : '#ffffff';
  const textPrimary = enableDarkTheme ? '#e8ecf4' : '#1a2035';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#757575';
  const accent = '#5E85F0';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '580px',
        background: bg,
        gap: '32px',
        px: '60px',
        py: '48px',
      }}
    >
      {/* Radar scanner */}
      <Box sx={{ position: 'relative', width: 220, height: 220 }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px dashed ${accent}55`,
            '@keyframes ai-spin-slow': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'ai-spin-slow 8s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: '18px',
            borderRadius: '50%',
            border: `1.5px solid ${accent}44`,
            '@keyframes ai-ring-pulse': {
              '0%,100%': { opacity: 0.4, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.04)' },
            },
            animation: 'ai-ring-pulse 2.4s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: '38px',
            borderRadius: '50%',
            border: `1px solid ${accent}33`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `conic-gradient(from 0deg, transparent 300deg, ${accent}55 360deg)`,
            '@keyframes ai-radar-spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'ai-radar-spin 2s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '10%',
            right: '10%',
            height: '1px',
            background: `${accent}33`,
            transform: 'translateY(-50%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '10%',
            bottom: '10%',
            width: '1px',
            background: `${accent}33`,
            transform: 'translateX(-50%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: accent,
            transform: 'translate(-50%, -50%)',
            '@keyframes ai-dot-pulse': {
              '0%,100%': { boxShadow: `0 0 0 0 ${accent}88` },
              '50%': { boxShadow: `0 0 0 8px ${accent}00` },
            },
            animation: 'ai-dot-pulse 1.6s ease-in-out infinite',
          }}
        />
      </Box>

      {/* Diagnostic step */}
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: accent,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          transition: 'opacity 0.3s ease',
          opacity: diagVisible ? 1 : 0,
          minHeight: '18px',
          textAlign: 'center',
        }}
      >
        {t(`ai_diag_step_${diagStep}`, { defaultValue: DIAG_STEPS[diagStep] })}
      </Typography>

      {/* Insight card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '560px',
          borderRadius: '10px',
          overflow: 'hidden',
          background: surface,
          boxShadow: enableDarkTheme
            ? '0 2px 16px rgba(0,0,0,0.45)'
            : '0 2px 16px rgba(0,0,0,0.08)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          opacity: insightVisible ? 1 : 0,
          transform: insightVisible ? 'translateY(0)' : 'translateY(6px)',
        }}
      >
        <Box
          sx={{
            background: severityColor,
            px: '12px',
            py: '6px',
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {t(`severity_${insight.severity}`, {
              defaultValue: String(insight.severity).toUpperCase(),
            })}{' '}
            {'·'}{' '}
            {t('fleet_analytics_title', {
              defaultValue: 'FLEET ANALYTICS',
            })}
          </Typography>
        </Box>
        <Box sx={{ p: '14px 16px' }}>
          <Typography
            sx={{
              fontSize: '30px',
              fontWeight: 800,
              color: severityColor,
              lineHeight: 1,
              mb: '4px',
            }}
          >
            {insight.stat}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: textPrimary,
              mb: '6px',
            }}
          >
            {t(`ai_insight_headline_${insightIndex}`, {
              defaultValue: insight.headline,
            })}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: textSecondary,
              lineHeight: 1.6,
            }}
          >
            {t(`ai_insight_detail_${insightIndex}`, {
              defaultValue: insight.detail,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Progress dots */}
      <Box sx={{ display: 'flex', gap: '6px' }}>
        {LOADING_INSIGHTS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === insightIndex ? '18px' : '6px',
              height: '6px',
              borderRadius: '3px',
              background:
                i === insightIndex
                  ? accent
                  : enableDarkTheme
                  ? 'rgba(255,255,255,0.15)'
                  : '#CBD2DC',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCards({
  data,
  enableDarkTheme,
  unitLabels,
}: {
  data: FuelAiAnalysisData;
  enableDarkTheme: boolean;
  unitLabels: FuelUnitLabels;
}) {
  const { t } = useTranslation();
  const cellBg = enableDarkTheme ? 'rgba(255,255,255,0.05)' : '#F5F7FA';
  const label = enableDarkTheme ? '#8A92A0' : '#616161';
  const value = enableDarkTheme ? '#fff' : '#212121';
  const sub = enableDarkTheme ? '#5A6370' : '#9E9E9E';

  const totalFuelConsumed =
    data.fleet_verdicts.haul_trucks.total_fuel_consumed +
    data.fleet_verdicts.loaders.total_fuel_consumed;

  const bestAsset = data.assets.reduce(
    (best, a) => (a.fuel_score > best.fuel_score ? a : best),
    data.assets[0]
  );
  const worstAsset = data.assets.reduce(
    (worst, a) => (a.fuel_score < worst.fuel_score ? a : worst),
    data.assets[0]
  );
  const bestGrade = bestAsset
    ? getGradeColors(bestAsset.fuel_score, enableDarkTheme)
    : null;

  const tpgDelta =
    data.fleet_verdicts.haul_trucks.avg_tpg -
    data.fleet_verdicts.haul_trucks.fleet_avg_tpg;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        mb: '16px',
      }}
    >
      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('total_fuel_consumed')}
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: value }}>
          {totalFuelConsumed.toLocaleString()} {unitLabels.fuel}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {data.assets.length} {t('loaders_combined')}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('haul_truck_avg_tpg')}
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: value }}>
          {data.fleet_verdicts.haul_trucks.avg_tpg}{' '}
          {data.meta.benchmark.haul_truck.unit}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {tpgDelta > 0 ? '+' : ''}
          {tpgDelta.toFixed(1)} {t('vs_fleet_benchmark')}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('most_fuel_efficient')}
        </Typography>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: bestGrade?.bar ?? value,
          }}
        >
          {bestAsset?.bumper_id ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {bestAsset
            ? `${t('grade')} ${bestAsset.grade} · ${bestAsset.fuel_score}/100`
            : ''}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('least_fuel_efficient')}
        </Typography>
        <Typography
          sx={{ fontSize: '18px', fontWeight: 500, color: '#E24B4A' }}
        >
          {worstAsset?.bumper_id ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {worstAsset
            ? `${t('grade')} ${worstAsset.grade} · ${worstAsset.fuel_score}/100`
            : ''}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Verdict Cards ────────────────────────────────────────────────────────────

function VerdictCards({
  data,
  enableDarkTheme,
  unitLabels,
}: {
  data: FuelAiAnalysisData;
  enableDarkTheme: boolean;
  unitLabels: FuelUnitLabels;
}) {
  const { t } = useTranslation();
  const cardBg = enableDarkTheme ? 'rgba(255,255,255,0.03)' : '#FFFFFF';
  const savingsBg = enableDarkTheme ? 'rgba(99,153,34,0.14)' : '#EAF3DE';
  const savingsText = enableDarkTheme ? '#9AD16B' : '#27500A';
  const border = enableDarkTheme ? '#2F445E' : '#E0E4EC';
  const textPri = enableDarkTheme ? '#fff' : '#212121';
  const textSec = enableDarkTheme ? '#8A92A0' : '#616161';

  const trucks = data.assets.filter((a) => a.asset_type === 'haul_truck');
  const loaders = data.assets.filter((a) => a.asset_type === 'loader');
  const bestTruck = trucks.reduce(
    (b, a) => (a.fuel_score > b.fuel_score ? a : b),
    trucks[0]
  );
  const worstTruck = trucks.reduce(
    (w, a) => (a.fuel_score < w.fuel_score ? a : w),
    trucks[0]
  );
  const bestLoader = loaders.reduce(
    (b, a) => (a.fuel_score > b.fuel_score ? a : b),
    loaders[0]
  );
  const worstLoader = loaders.reduce(
    (w, a) => (a.fuel_score < w.fuel_score ? a : w),
    loaders[0]
  );

  const truckPct =
    data.fleet_verdicts.haul_trucks.fleet_avg_tpg > 0
      ? Math.round(
          (data.fleet_verdicts.haul_trucks.avg_tpg /
            data.fleet_verdicts.haul_trucks.fleet_avg_tpg) *
            100
        )
      : 100;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        alignItems: 'start',
        gap: '10px',
        mb: '8px',
      }}
    >
      {/* Hauler verdict */}
      <Box
        sx={{
          minWidth: 0,
          background: cardBg,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: '14px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '10px',
          }}
        >
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: textPri }}
          >
            {t('haul_truck_fuel_verdict')}
          </Typography>
          <Chip
            label={`${truckPct}${t('of_benchmark')}`}
            size="small"
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              background:
                truckPct >= 100
                  ? '#EAF3DE'
                  : truckPct >= 85
                  ? '#FAEEDA'
                  : '#FCEBEB',
              color:
                truckPct >= 100
                  ? '#27500A'
                  : truckPct >= 85
                  ? '#854F0B'
                  : '#A32D2D',
              height: '22px',
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            mb: '10px',
          }}
        >
          <Box
            sx={{ background: '#EAF3DE', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#3B6D11', mb: '1px' }}>
              {t('best_asset')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#27500A' }}
            >
              {bestTruck?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FCEBEB', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#A32D2D', mb: '1px' }}>
              {t('drag_asset')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#A32D2D' }}
            >
              {worstTruck?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FAEEDA', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#854F0B', mb: '1px' }}>
              {t('avg_tpg')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#633806' }}
            >
              {data.fleet_verdicts.haul_trucks.avg_tpg}{' '}
              {data.meta.benchmark.haul_truck.unit}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '12px', color: textSec, lineHeight: 1.65 }}>
          {data.fleet_verdicts.haul_trucks.summary}
        </Typography>
        {data.fleet_verdicts.haul_trucks.total_potential_fuel_savings !=
          null && (
          <Box
            sx={{
              background: savingsBg,
              borderRadius: '7px',
              p: '8px 10px',
              mt: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '11px', color: savingsText }}>
              {t('fuel_usage_total_potential_fuel_savings')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 700, color: savingsText }}
            >
              {data.fleet_verdicts.haul_trucks.total_potential_fuel_savings.toLocaleString()}{' '}
              {unitLabels.fuel}
            </Typography>
          </Box>
        )}
        {/* {data.fleet_verdicts.haul_trucks.fuel_by_road && (
          <RoadFuelUsageSection
            roads={data.fleet_verdicts.haul_trucks.fuel_by_road}
            fuelUnit={unitLabels.fuel}
            enableDarkTheme={enableDarkTheme}
          />
        )} */}
        <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
          <FeedbackWidget
            featureName="fuelAIAnalysis"
            metadata={{
              context: 'fleetVerdict_haulTrucks',
              period: data.meta.period,
              total_fuel_consumed: String(
                data.fleet_verdicts.haul_trucks.total_fuel_consumed
              ),
              avg_tpg: String(data.fleet_verdicts.haul_trucks.avg_tpg),
              fleet_avg_tpg: String(
                data.fleet_verdicts.haul_trucks.fleet_avg_tpg
              ),
              summary: data.fleet_verdicts.haul_trucks.summary,
              total_potential_fuel_savings: String(
                data.fleet_verdicts.haul_trucks.total_potential_fuel_savings ??
                  ''
              ),
            }}
          />
        </Box>
      </Box>

      {/* Loader verdict */}
      <Box
        sx={{
          minWidth: 0,
          background: cardBg,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: '14px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '10px',
          }}
        >
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: textPri }}
          >
            {t('loader_fuel_verdict')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            mb: '10px',
          }}
        >
          <Box
            sx={{ background: '#EAF3DE', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#3B6D11', mb: '1px' }}>
              {t('best_loader')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#27500A' }}
            >
              {bestLoader?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FCEBEB', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#A32D2D', mb: '1px' }}>
              {t('worst_loader')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#A32D2D' }}
            >
              {worstLoader?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FAEEDA', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#854F0B', mb: '1px' }}>
              {t('avg_gph')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#633806' }}
            >
              {data.fleet_verdicts.loaders.avg_gph}{' '}
              {data.meta.benchmark.loader.unit}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '12px', color: textSec, lineHeight: 1.65 }}>
          {data.fleet_verdicts.loaders.summary}
        </Typography>
        {data.fleet_verdicts.loaders.total_potential_fuel_savings != null && (
          <Box
            sx={{
              background: savingsBg,
              borderRadius: '7px',
              p: '8px 10px',
              mt: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '11px', color: savingsText }}>
              {t('fuel_usage_total_potential_fuel_savings')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 700, color: savingsText }}
            >
              {data.fleet_verdicts.loaders.total_potential_fuel_savings.toLocaleString()}{' '}
              {unitLabels.fuel}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
          <FeedbackWidget
            featureName="fuelAIAnalysis"
            metadata={{
              context: 'fleetVerdict_loaders',
              period: data.meta.period,
              total_fuel_consumed: String(
                data.fleet_verdicts.loaders.total_fuel_consumed
              ),
              avg_gph: String(data.fleet_verdicts.loaders.avg_gph),
              fleet_median_gph: String(
                data.fleet_verdicts.loaders.fleet_median_gph
              ),
              summary: data.fleet_verdicts.loaders.summary,
              total_potential_fuel_savings: String(
                data.fleet_verdicts.loaders.total_potential_fuel_savings ?? ''
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  enableDarkTheme: boolean;
}

const AiAnalysisModal = ({ open, onClose, enableDarkTheme }: Props) => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<FuelAiAnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [haulerPlanOpen, setHaulerPlanOpen] = useState(false);
  const [loaderPlanOpen, setLoaderPlanOpen] = useState(false);

  const api = new AiAnalyticsAPI();

  const fetchAnalysis = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await api.getFuelAnalysis();
      setData(result as FuelAiAnalysisData);
    } catch {
      setError(t('ai_analysis_failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      void fetchAnalysis();
    }
  }, [open]);

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const headerBg = enableDarkTheme ? '#0a1929' : '#FFFFFF';
  const borderColor = enableDarkTheme ? '#1A3050' : '#E0E4EC';
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';

  const trucks =
    data?.assets.filter((a) => a.asset_type === 'haul_truck') ?? [];
  const loaders = data?.assets.filter((a) => a.asset_type === 'loader') ?? [];
  const unitLabels =
    data?.meta.unit_labels ??
    (unitSystem === 'imperial' ? IMPERIAL_UNIT_LABELS : METRIC_UNIT_LABELS);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(1100px, 96vw)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: headerBg,
              borderBottom: `1px solid ${borderColor}`,
              px: '20px',
              py: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 600, color: textPrimary }}
              >
                {t('ai_fuel_analysis')}
              </Typography>
              {data && (
                <Box
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    px: '10px',
                    py: '3px',
                    borderRadius: '20px',
                    border: '0.5px solid #AFA9EC',
                    background: '#EEEDFE',
                    color: '#3C3489',
                  }}
                >
                  {data.meta.period}
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box
            sx={{
              background: bg,
              overflowY: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: enableDarkTheme ? '#2F445E' : '#CBD2DC',
                borderRadius: '3px',
              },
            }}
          >
            {isLoading && <LoadingScreen enableDarkTheme={enableDarkTheme} />}

            {error && !isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <Typography
                  sx={{
                    color: ThemePalette.typography.danger,
                    fontSize: '14px',
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            {data && !isLoading && (
              <Box sx={{ p: '20px' }}>
                {/* Period subtitle */}
                <Typography
                  sx={{ fontSize: '12px', color: textSecondary, mb: '14px' }}
                >
                  {data.meta.total_days}
                  {t('day_period_click_any_card_to_expand')}
                </Typography>

                {/* Summary cards */}
                <SummaryCards
                  data={data}
                  enableDarkTheme={enableDarkTheme}
                  unitLabels={unitLabels}
                />

                {/* Fleet verdicts */}
                <VerdictCards
                  data={data}
                  enableDarkTheme={enableDarkTheme}
                  unitLabels={unitLabels}
                />

                {/* Fuel usage by driving event */}
                <EventFuelExtremesSection
                  haulTrucks={data.fleet_verdicts.haul_trucks}
                  trucks={trucks}
                  unitLabels={unitLabels}
                  enableDarkTheme={enableDarkTheme}
                />

                {/* Haul trucks section */}
                {trucks.length > 0 && (
                  <>
                    <Divider sx={{ borderColor, my: '14px' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('haul_trucks_fuel_ranked')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setHaulerPlanOpen(true);
                        }}
                        sx={{
                          fontSize: '11px',
                          py: '3px',
                          px: '12px',
                          textTransform: 'none',
                          borderRadius: '20px',
                          borderColor: enableDarkTheme
                            ? 'rgba(94,133,240,0.4)'
                            : 'rgba(94,133,240,0.5)',
                          color: '#5E85F0',
                          '&:hover': {
                            borderColor: '#5E85F0',
                            background: 'rgba(94,133,240,0.08)',
                          },
                        }}
                      >
                        {t('view_all_action_plans')}
                      </Button>
                    </Box>
                    {trucks
                      .sort((a, b) => a.rank - b.rank)
                      .map((asset) => (
                        <AssetCard
                          key={asset.bumper_id}
                          asset={asset}
                          enableDarkTheme={enableDarkTheme}
                          period={data.meta.period}
                          unitLabels={unitLabels}
                          benchmark={data.meta.benchmark}
                        />
                      ))}
                  </>
                )}

                {/* Loaders section */}
                {loaders.length > 0 && (
                  <>
                    <Divider sx={{ borderColor, my: '14px' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('loaders_fuel_ranked')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setLoaderPlanOpen(true);
                        }}
                        sx={{
                          fontSize: '11px',
                          py: '3px',
                          px: '12px',
                          textTransform: 'none',
                          borderRadius: '20px',
                          borderColor: enableDarkTheme
                            ? 'rgba(94,133,240,0.4)'
                            : 'rgba(94,133,240,0.5)',
                          color: '#5E85F0',
                          '&:hover': {
                            borderColor: '#5E85F0',
                            background: 'rgba(94,133,240,0.08)',
                          },
                        }}
                      >
                        {t('view_all_action_plans')}
                      </Button>
                    </Box>
                    {loaders
                      .sort((a, b) => a.rank - b.rank)
                      .map((asset) => (
                        <AssetCard
                          key={asset.bumper_id}
                          asset={asset}
                          enableDarkTheme={enableDarkTheme}
                          period={data.meta.period}
                          unitLabels={unitLabels}
                          benchmark={data.meta.benchmark}
                        />
                      ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <FleetActionPlansDialog
        open={haulerPlanOpen}
        onClose={() => {
          setHaulerPlanOpen(false);
        }}
        title={t('haul_trucks_all_action_plans')}
        assets={trucks}
      />
      <FleetActionPlansDialog
        open={loaderPlanOpen}
        onClose={() => {
          setLoaderPlanOpen(false);
        }}
        title={t('loaders_all_action_plans')}
        assets={loaders}
      />
    </>
  );
};

export default AiAnalysisModal;
